import { Checkbox, FormLabel, HiddenInputField, Textinput } from "shared";
import {
    ITemplateComponent,
    ITemplateSaveAndTicketComponent
} from "entities/template";
import React, { useEffect } from "react";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useComponentValidateFunction } from "../../../../lib/hooks/useComponentValidateFunction";
import { VisibilityField } from "../../form_fields/visibility_field/visibility_field";
import { TooltipField } from "../../form_fields/tooltip_field/tooltip_field";
import { IssueTypeSelect } from "../../form_fields/issue_type_select/issue_type_select";
import styles from "./component_form_save_and_ticket.module.scss";

interface IComponentFormSaveAndTicketProps {
    component: ITemplateSaveAndTicketComponent;
    componentList: ITemplateComponent[];
    onTemplatePasswordChange: (value?: string) => void;
    templatePassword?: string;
    enLanguageEnabled: boolean;
    triggerValidate: number;
    onChange: (component: ITemplateComponent) => void;

}

export const ComponentFormSaveAndTicket: React.FC<IComponentFormSaveAndTicketProps> = React.memo(({
                                                                                                      component,
                                                                                                      componentList,
                                                                                                      enLanguageEnabled,
                                                                                                      onTemplatePasswordChange,
                                                                                                      templatePassword,
                                                                                                      triggerValidate,
                                                                                                      onChange
                                                                                                  }) => {
    const t = useComponentsTranslations();
    const { requiredFieldValidation, requiredPasswordValidation } = useComponentValidateFunction();

    const onFieldChange = (value: Partial<ITemplateSaveAndTicketComponent>) => {
        onChange({
            ...component,
            ...value
        });
    };

    return <div className={styles.container}>
        <FormLabel title={t("trackerQueue")} required={true}>
            <Textinput size="m"
                       value={component.startrekQueue ? component.startrekQueue : ""}
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={requiredFieldValidation}
                       onChange={(e) => onFieldChange({
                           startrekQueue: e.target.value,
                           issueType: e.target.value !== component.startrekQueue ? undefined : component.issueType
                       })}
                       view="outline"/>
            <div className={styles.subLabel}>{"* " + t("trackerQueueDescription")}</div>
        </FormLabel>
        <IssueTypeSelect
            value={component.issueType}
            startrekQueue={component.startrekQueue}
            onChange={(val) => onFieldChange({
                issueType: val
            })}
        />
        <VisibilityField
            component={component}
            componentList={componentList}
            onChange={onFieldChange}/>
        <TooltipField
            component={component}
            enLanguageEnabled={enLanguageEnabled}
            onChange={onFieldChange}
        />
        <Checkbox
            label={t("rowGroup")}
            size="m"
            onChange={() => onFieldChange({ isRowGroup: !component.isRowGroup })}
            checked={component.isRowGroup}
        />
        <div className={styles.checkboxGroup}>
            <Checkbox
                className={styles.firstCheckbox}
                label={t("savePdf")}
                size="m"
                onChange={() => onFieldChange({ isDocPdf: component.isDocPdf === false })}
                checked={component.isDocPdf !== false}
            />
            <Checkbox
                label={t("saveDocument")}
                size="m"
                onChange={() => onFieldChange({ isDocRtf: component.isDocRtf === false })}
                checked={component.isDocRtf !== false}
            />
        </div>
        <div className={styles.checkboxGroup}>
            <Checkbox
                className={styles.firstCheckbox}
                label={t("ticketWithPdf")}
                size="m"
                onChange={() => onFieldChange({ isTicketPdf: component.isTicketPdf === false })}
                checked={component.isTicketPdf !== false}
            />
            <Checkbox
                label={t("ticketWithDocument")}
                size="m"
                onChange={() => onFieldChange({ isTicketRtf: component.isTicketRtf === false })}
                checked={component.isTicketRtf !== false}
            />
        </div>
        <Checkbox
            label={t("protectDocument")}
            size="m"
            onChange={() => {
                onFieldChange({
                    isProtected: !component.isProtected,
                    isDocPdfNopass: false,
                    isDocRtfNopass: false,
                    isTicketPdfNopass: false,
                    isTicketRtfNopass: false
                });
                onTemplatePasswordChange(undefined);
            }}
            checked={!!component.isProtected}
        />
        {component.isProtected && <FormLabel title={t("passwordToEditDocument")} required={true}>
            <HiddenInputField
                validate={true}
                triggerValidate={triggerValidate}
                validateFunc={requiredPasswordValidation}
                name="pdfAdminPassword"
                onChange={onTemplatePasswordChange}
                value={templatePassword}/>
        </FormLabel>}
        {component.isProtected && <div className={styles.checkboxGroup}>
            <Checkbox
                className={styles.firstCheckbox}
                label={t("savePdfUnprotected")}
                size="m"
                onChange={() => onFieldChange({ isDocPdfNopass: !component.isDocPdfNopass })}
                checked={!!component.isDocPdfNopass}
            />
            <Checkbox
                label={t("saveDocumentUnprotected")}
                size="m"
                onChange={() => onFieldChange({ isDocRtfNopass: !component.isDocRtfNopass })}
                checked={!!component.isDocRtfNopass}
            />
        </div>}
        {component.isProtected && <div className={styles.checkboxGroup}>
            <Checkbox
                className={styles.firstCheckbox}
                label={t("ticketWithPdfUnprotected")}
                size="m"
                onChange={() => onFieldChange({ isTicketPdfNopass: !component.isTicketPdfNopass })}
                checked={!!component.isTicketPdfNopass}
            />
            <Checkbox
                label={t("ticketWithDocumentUnprotected")}
                size="m"
                onChange={() => onFieldChange({ isTicketRtfNopass: !component.isTicketRtfNopass })}
                checked={!!component.isTicketRtfNopass}
            />
        </div>}
        <div className={styles.checkboxGroup}>
            <Checkbox
                className={styles.firstCheckbox}
                label={t("createTicketAndSavePdfFile")}
                size="m"
                onChange={() => onFieldChange({ isDocAndTicketPdf: !component.isDocAndTicketPdf })}
                checked={!!component.isDocAndTicketPdf}
            />
            <Checkbox
                label={t("createTicketAndSaveWordFile")}
                size="m"
                onChange={() => onFieldChange({ isDocAndTicketRtf: !component.isDocAndTicketRtf })}
                checked={!!component.isDocAndTicketRtf}
            />
        </div>
        <Checkbox
            label={t("createTicketPdfWord")}
            size="m"
            onChange={() => onFieldChange({ isTicketPdfRtfNopass: !component.isTicketPdfRtfNopass })}
            checked={!!component.isTicketPdfRtfNopass}
        />
        <Checkbox
            label={t("attachFileToTicket")}
            size="m"
            onChange={() => onFieldChange({ isUploadFile: !component.isUploadFile })}
            checked={!!component.isUploadFile}
        />
    </div>;
});
